/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css } from 'lit-element';

/**
 * Input CSS
 */
export const styles = css`
  :host {
    display: block;
  }

  .form-control .form-control__label {
    display: none;
  }

  .form-control .form-control__help-text {
    display: none;
  }

  /* Label */
  .form-control--has-label .form-control__label {
    display: inline-block;
    color: inherit;
    margin-bottom: 0.125rem;
  }

  .form-control--has-label.form-control--small .form-control__label {
    font-size: 0.875rem;
  }

  .form-control--has-label.form-control--medium .form-control__label {
    font-size: 1rem;
  }

  .form-control--has-label.form-control--large .form-control__label {
    font-size: 1.25rem;
  }

  :host([required]) .form-control--has-label .form-control__label::after {
    content: '*';
    margin-inline-start: -2px;
    color: undefined;
  }

  /* Help text */
  .form-control--has-help-text .form-control__help-text {
    display: block;
    color: hsl(240 3.8% 46.1%);
    margin-top: 0.125rem;
  }

  .form-control--has-help-text.form-control--small .form-control__help-text {
    font-size: 0.75rem;
  }

  .form-control--has-help-text.form-control--medium .form-control__help-text {
    font-size: 0.875rem;
  }

  .form-control--has-help-text.form-control--large .form-control__help-text {
    font-size: 1rem;
  }

  .form-control--has-help-text.form-control--radio-group
    .form-control__help-text {
    margin-top: 0.25rem;
  }

  .input {
    flex: 1 1 auto;
    display: inline-flex;
    align-items: stretch;
    justify-content: start;
    position: relative;
    width: 100%;
    font-family: inherit;
    font-weight: 400;
    letter-spacing: normal;
    vertical-align: middle;
    overflow: hidden;
    cursor: text;
    transition: 150ms color, 150ms border, 150ms box-shadow,
      150ms background-color;
  }

  /* Standard inputs */
  .input--standard {
    background-color: hsl(0, 0%, 100%);
    border: solid 1px #c7ced4;
  }

  .input--standard:hover:not(.input--disabled) {
    background-color: hsl(0, 0%, 100%);
    border-color: #000000;
  }

  .input--standard.input--focused:not(.input--disabled) {
    background-color: hsl(0, 0%, 100%);
    border-color: #005ea6;
    box-shadow: 0 0 0 3px hsl(198.6 88.7% 48.4% / 40%);
  }

  .input--standard.input--focused:not(.input--disabled) .input__control {
    color: hsl(240 5.3% 26.1%);
  }

  .input--standard.input--disabled {
    background-color: hsl(240 4.8% 95.9%);
    border-color: hsl(240 4.9% 83.9%);
    opacity: 0.5;
    cursor: not-allowed;
  }

  .input--standard.input--disabled .input__control {
    color: hsl(240 5.9% 10%);
  }

  .input--standard.input--disabled .input__control::placeholder {
    color: hsl(240 5.2% 33.9%);
  }

  .input--standard.input--warning {
    border-color: #E5BF43;
  }

  .input--standard.input--error {
    border-color: #E5434D;
  }

  .input--standard.input--success {
    border-color: #43E5C0;
  }

  /* Filled inputs */
  .input--filled {
    border: none;
    background-color: hsl(240 4.8% 95.9%);
    color: hsl(240 5.3% 26.1%);
  }

  .input--filled:hover:not(.input--disabled) {
    background-color: hsl(240 4.8% 95.9%);
  }

  .input--filled.input--focused:not(.input--disabled) {
    background-color: hsl(240 4.8% 95.9%);
    outline: solid 3px hsl(200.4 98% 39.4%);
    outline-offset: 1px;
  }

  .input--filled.input--disabled {
    background-color: hsl(240 4.8% 95.9%);
    opacity: 0.5;
    cursor: not-allowed;
  }

  .input__control {
    flex: 1 1 auto;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    min-width: 0;
    height: 100%;
    color: hsl(240 5.3% 26.1%);
    border: none;
    background: none;
    box-shadow: none;
    padding: 0;
    margin: 0;
    cursor: inherit;
    -webkit-appearance: none;
  }

  .input__control::-webkit-search-decoration,
  .input__control::-webkit-search-cancel-button,
  .input__control::-webkit-search-results-button,
  .input__control::-webkit-search-results-decoration {
    -webkit-appearance: none;
  }

  .input__control:-webkit-autofill {
    box-shadow: 0 0 0 3.125rem hsl(0, 0%, 100%) inset !important;
    -webkit-text-fill-color: hsl(198.6 88.7% 48.4%);
    caret-color: hsl(240 5.3% 26.1%);
  }

  .input__control:-webkit-autofill:hover {
    box-shadow: 0 0 0 3.125rem hsl(0, 0%, 100%) inset !important;
    -webkit-text-fill-color: hsl(198.6 88.7% 48.4%);
    caret-color: hsl(240 5.3% 26.1%);
  }

  .input__control:-webkit-autofill:focus {
    box-shadow: 0 0 0 3.125rem hsl(0, 0%, 100%) inset !important;
    -webkit-text-fill-color: hsl(198.6 88.7% 48.4%);
    caret-color: hsl(240 5.3% 26.1%);
  }

  .input__control:-webkit-autofill:active {
    box-shadow: 0 0 0 3.125rem hsl(0, 0%, 100%) inset !important;
    -webkit-text-fill-color: hsl(198.6 88.7% 48.4%);
    caret-color: hsl(240 5.3% 26.1%);
  }

  .input--filled .input__control:-webkit-autofill {
    box-shadow: 0 0 0 3.125rem hsl(240 4.8% 95.9%) inset !important;
  }

  .input--filled .input__control:-webkit-autofill:hover {
    box-shadow: 0 0 0 3.125rem hsl(240 4.8% 95.9%) inset !important;
  }

  .input--filled .input__control:-webkit-autofill:focus {
    box-shadow: 0 0 0 3.125rem hsl(240 4.8% 95.9%) inset !important;
  }

  .input--filled .input__control:-webkit-autofill:active {
    box-shadow: 0 0 0 3.125rem hsl(240 4.8% 95.9%) inset !important;
  }

  .input__control::placeholder {
    color: hsl(240 3.8% 46.1%);
    user-select: none;
  }

  .input:hover:not(.input--disabled) .input__control {
    color: hsl(240 5.3% 26.1%);
  }

  .input__control:focus {
    outline: none;
  }

  .input__prefix,
  .input__suffix {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    cursor: default;
  }

  .input__prefix::slotted(nile-icon) {
    color: hsl(240 3.8% 46.1%);
  }

  .input__suffix::slotted(nile-icon) {
    color: hsl(240 3.8% 46.1%);
  }

  /*
   * Size modifiers
   */

  .input--small {
    border-radius: 0.25rem;
    font-size: 0.875rem;
    height: 1.875rem;
  }

  .input--small .input__control {
    height: calc(1.875rem - 1px * 2);
    padding: 0 0.75rem;
  }

  .input--small .input__clear {
    width: calc(1em + 0.75rem * 2);
  }

  .input--small .input__password-toggle {
    width: calc(1em + 0.75rem * 2);
  }

  .input--small .input__prefix::slotted(*) {
    margin-inline-start: 0.75rem;
  }

  .input--small .input__suffix::slotted(*) {
    margin-inline-end: 0.75rem;
  }

  .input--medium {
    border-radius: 0.25rem;
    font-size: 1rem;
    height: 38px;
  }

  .input--medium .input__control {
    height: calc(42px - 1px * 2);
    padding: 0 1rem;
  }

  .input--medium .input__clear {
    width: calc(1em + 1rem * 2);
  }

  .input--medium .input__password-toggle {
    width: calc(1em + 1rem * 2);
  }

  .input--medium .input__prefix::slotted(*) {
    margin-inline-start: 1rem;
  }

  .input--medium .input__suffix::slotted(*) {
    margin-inline-end: 1rem;
  }

  .input--large {
    border-radius: 0.25rem;
    font-size: 1.25rem;
    height: 3.125rem;
  }

  .input--large .input__control {
    height: calc(3.125rem - 1px * 2);
    padding: 0 1.25rem;
  }

  .input--large .input__clear {
    width: calc(1em + 1.25rem * 2);
  }

  .input--large .input__password-toggle {
    width: calc(1em + 1.25rem * 2);
  }

  .input--large .input__prefix::slotted(*) {
    margin-inline-start: 1.25rem;
  }

  .input--large .input__suffix::slotted(*) {
    margin-inline-end: 1.25rem;
  }

  /*
   * Pill modifier
   */

  .input--pill.input--small {
    border-radius: 1.875rem;
  }

  .input--pill.input--medium {
    border-radius: 2.5rem;
  }

  .input--pill.input--large {
    border-radius: 3.125rem;
  }

  /*
   * Clearable + Password Toggle
   */

  .input__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: inherit;
    color: hsl(240 3.8% 46.1%);
    border: none;
    background: none;
    padding: 0;
    transition: 150ms color;
    cursor: pointer;
  }

  .input__password-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: inherit;
    color: hsl(240 3.8% 46.1%);
    border: none;
    background: none;
    padding: 0;
    transition: 150ms color;
    cursor: pointer;
  }

  .input__clear:hover {
    color: hsl(240 5.2% 33.9%);
  }

  .input__password-toggle:hover {
    color: hsl(240 5.2% 33.9%);
  }

  .input__clear:focus,
  .input__password-toggle:focus {
    outline: none;
  }

  .input--empty .input__clear {
    visibility: hidden;
  }

  /* Don't show the browser's password toggle in Edge */
  ::-ms-reveal {
    display: none;
  }

  /* Hide the built-in number spinner */
  .input--no-spin-buttons input[type='number']::-webkit-outer-spin-button,
  .input--no-spin-buttons input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    display: none;
  }

  .input--no-spin-buttons input[type='number'] {
    -moz-appearance: textfield;
  }
`;

export default [styles];
